import { useFlags } from '@atlaskit/flag';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { token } from '@atlaskit/tokens';
import { di } from 'react-magnetic-di';
import { graphql, useMutation, useQueryLoader } from 'react-relay';
import { useRouter } from 'react-resource-router';

import { createFlowRoute } from '../../../routes/utils';
import { getHostname } from '../../../utils';
import { useAnalyticsWebClient } from '../../../utils/analytics/context';
import { useAuthData } from '../../../utils/auth-context';
import { useTenantData } from '../../../utils/tenant-context';
import type { flowsQuery } from '../layout-sidebar/components/__generated__/flowsQuery.graphql';
import sidebarQuery from '../layout-sidebar/components/__generated__/flowsQuery.graphql';
import { serializeAdditionalInfo } from '../utils/additional-info-json';
import { serializeIssue } from '../utils/issue-json';
import type { SessionIssue } from '../utils/issue-json';

import type {
	useCreateFlowMutation,
	useCreateFlowMutation$data,
} from './__generated__/useCreateFlowMutation.graphql';

type CreateSessionParams = {
	issueARI?: string;
	repoUrl: string;
	issue?: SessionIssue;
	branchName: string;
	prompt?: string;
	onSuccess?: () => void;
};

export const useCreateFlowSession = (): [(params: CreateSessionParams) => void, boolean] => {
	di();

	const [, routerActions] = useRouter();

	const { tenantContexts } = useTenantData();
	const { cloudId } = tenantContexts[0];

	const { user } = useAuthData();
	const [, loadQuery] = useQueryLoader<flowsQuery>(sidebarQuery);

	const { showFlag } = useFlags();

	const refreshSidebarQuery = () => {
		if (user?.accountId) {
			loadQuery(
				{
					creator: user.accountId,
					cloudId,
				},
				{
					fetchPolicy: 'store-and-network',
				},
			);
		}
	};

	const [commit, isPending] = useMutation<useCreateFlowMutation>(graphql`
		mutation useCreateFlowMutation(
			$cloudId: String!
			$issueARI: ID
			$repoUrl: URL!
			$createdBy: String!
			$jiraHost: String!
			$jiraIssueJSON: String
			$additionalInfoJSON: String!
		) {
			devai_flowCreate(
				issueARI: $issueARI
				cloudId: $cloudId
				repoUrl: $repoUrl
				createdBy: $createdBy
				jiraHost: $jiraHost
				jiraIssueJSON: $jiraIssueJSON
				additionalInfoJSON: $additionalInfoJSON
			) @optIn(to: "DevAiFlowCreate") {
				id
			}
		}
	`);

	const analyticsWebClient = useAnalyticsWebClient();

	const createSession = ({
		issueARI,
		repoUrl,
		issue,
		branchName,
		prompt,
		onSuccess,
	}: CreateSessionParams): void => {
		const additionalInfo = {
			gitUserName: user?.name ?? '',
			gitUserEmail: user?.email ?? '',
			branchName,
			prompt: prompt ?? '',
		};

		commit({
			variables: {
				issueARI: issueARI ?? null,
				cloudId,
				createdBy: user?.accountId ?? '',
				repoUrl,
				jiraHost: getHostname(),
				jiraIssueJSON: issue ? serializeIssue(issue) : null,
				additionalInfoJSON: serializeAdditionalInfo(additionalInfo),
			},
			onCompleted: (response: useCreateFlowMutation$data) => {
				const sessionId = response.devai_flowCreate?.id;
				if (sessionId) {
					refreshSidebarQuery();
					const flowRoute = createFlowRoute(sessionId);
					routerActions.push(flowRoute);

					analyticsWebClient.sendTrackEvent({
						action: 'create',
						actionSubject: 'session',
						source: 'use-create-flow',
					});

					onSuccess?.();
				}
			},
			onError: (error) => {
				const dismiss = showFlag({
					id: 'create-flow-error',
					title: "Couldn't create a flow",
					description: error.message,
					icon: <WarningIcon label="Warning" primaryColor={token('color.icon.warning')} />,
					actions: [
						{
							content: 'Dismiss',
							onClick: () => dismiss(),
						},
					],
				});
			},
		});
	};

	return [createSession, isPending];
};
